import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DatePickerField from '../UI/DatePickerField';
import {
  Search,
  ChevronDown,
  ChevronUp,
  X,
  Clock,
  Users,
  Shield,
  FileText,
  Send,
  Save,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Briefcase,
  Sun,
  Info,
  Check,
  CalendarRange,
} from 'lucide-react';
import toast from '../../utils/toast';
import { staffAPI } from '../../services/api';
import SearchableMultiCombobox from '../UI/SearchableMultiCombobox';
import { toLocalISODate, parseLocalDate } from '../../utils/dateUtils';

const WORK_HOURS = [];
for (let h = 6; h <= 22; h++) WORK_HOURS.push(`${String(h).padStart(2, '0')}:00`);

const formatDateShort = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Asia/Singapore' });
};

const formatTimeLabel = (t) => {
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const calcShiftHours = (start, end, breakMin) => {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let startMins = sh * 60 + sm;
  let endMins = eh * 60 + em;
  if (endMins <= startMins) endMins += 24 * 60;
  const gross = (endMins - startMins) / 60;
  return { gross, net: gross - (breakMin || 0) / 60 };
};

// MOM Employment Act: 1h break per 6h continuous work. With 1h breaks
// the max sustainable shift is K=0 -> 6h, K=1 -> 13h, K=2 -> 20h, K=3 -> 27h.
// Rest day work is always a fresh continuous block (no day shift before
// it), so we just apply the bands to the shift duration directly.
// Mirrors RestDayWorkRequest.calculate_break_duration on the backend.
const calcMomBreakMinutes = (start, end) => {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let startMins = sh * 60 + sm;
  let endMins = eh * 60 + em;
  if (endMins <= startMins) endMins += 24 * 60;
  const shiftMins = endMins - startMins;
  if (shiftMins <= 360) return 0;
  if (shiftMins <= 780) return 60;
  if (shiftMins <= 1200) return 120;
  return 180;
};

// ─── Safety Personnel Selector (mirrors Nightwork pattern) ─────────────────────
const SafetyPersonnelSelect = ({ personnel, selected, onChange, loading }) => {
  const hasSelection = selected.length > 0;

  const toggle = (staffId) => {
    onChange(selected.includes(staffId) ? selected.filter(id => id !== staffId) : [...selected, staffId]);
  };

  return (
    <div className={`rounded-xl border-2 transition-colors ${hasSelection ? 'border-green-300 bg-green-50/30' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Shield className={`w-5 h-5 ${hasSelection ? 'text-green-600' : 'text-gray-400'}`} />
          <span className="text-sm font-medium text-gray-900">Safety Department</span>
        </div>
        {hasSelection ? (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3.5 h-3.5" />
            {selected.length} assigned
          </div>
        ) : (
          <span className="text-xs text-gray-500 font-medium">Optional</span>
        )}
      </div>
      <div className="p-2 max-h-48 overflow-y-auto">
        {loading ? (
          <div className="px-4 py-3 text-sm text-gray-500 text-center">Loading safety personnel...</div>
        ) : personnel?.length === 0 ? (
          <div className="px-4 py-3 text-sm text-gray-500 text-center">No safety personnel found</div>
        ) : (
          (personnel || []).map(p => {
            const checked = selected.includes(p.staff_id);
            return (
              <label
                key={p.staff_id}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${checked ? 'bg-green-100' : 'hover:bg-gray-50'}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(p.staff_id)}
                  className="rounded border-gray-300 text-green-600 focus-visible:ring-green-500"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-900">{p.first_name} {p.last_name}</span>
                </div>
                <span className="text-xs text-gray-400 font-mono flex-shrink-0">{p.staff_id}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};

// ─── Main Form ────────────────────────────────────────────────────────────────
const RestDayWorkRequestForm = ({ onSuccess, currentUser, editRequestId }) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  const isEditMode = !!editRequestId;

  const [formData, setFormData] = useState({
    project: '',
    project_name: '',
    description: '',
    date_selection_type: 'range',
    start_date: '',
    end_date: '',
    specific_dates: [],
    start_time: '08:00',
    end_time: '17:00',
    recovery_type: 'full_day',
    worker_ids: [],
    safety_ids: [],
    worker_date_overrides: {},
    safety_date_overrides: {},
    compliance_override_reason: '',
  });
  const [editLoaded, setEditLoaded] = useState(false);
  const [checkedWorkers, setCheckedWorkers] = useState(new Set());
  const [showBulkDatePicker, setShowBulkDatePicker] = useState(false);
  const [bulkStartDate, setBulkStartDate] = useState(null);
  const [bulkEndDate, setBulkEndDate] = useState(null);
  const [complianceWarnings, setComplianceWarnings] = useState([]);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const complianceTimerRef = useRef(null);

  // ─── Load existing request for edit mode ────────────────────────────────────
  const { data: editRequest } = useQuery({
    queryKey: ['restday-work-request', editRequestId],
    queryFn: () => staffAPI.getRestDayWorkRequest(editRequestId),
    select: r => r.data,
    enabled: isEditMode && !editLoaded,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (editRequest && !editLoaded) {
      setFormData({
        project: editRequest.project || '',
        project_name: editRequest.project_code
          ? `${editRequest.project_code} - ${editRequest.project_name}`
          : '',
        description: editRequest.description || '',
        date_selection_type: editRequest.date_selection_type || 'range',
        start_date: editRequest.start_date || '',
        end_date: editRequest.end_date || '',
        specific_dates: editRequest.specific_dates || [],
        start_time: editRequest.start_time?.slice(0, 5) || '08:00',
        end_time: editRequest.end_time?.slice(0, 5) || '17:00',
        recovery_type: editRequest.recovery_type || 'full_day',
        worker_ids: (editRequest.assignments || [])
          .filter(a => a.role === 'worker' && a.is_active)
          .map(a => a.employee_staff_id),
        safety_ids: (editRequest.assignments || [])
          .filter(a => a.role === 'safety' && a.is_active)
          .map(a => a.employee_staff_id),
        worker_date_overrides: Object.fromEntries(
          (editRequest.assignments || [])
            .filter(a => a.role === 'worker' && a.is_active && (a.effective_from || a.effective_until))
            .map(a => [a.employee_staff_id, {
              start_date: a.effective_from || editRequest.start_date || '',
              end_date: a.effective_until || editRequest.end_date || '',
            }])
        ),
        safety_date_overrides: Object.fromEntries(
          (editRequest.assignments || [])
            .filter(a => a.role === 'safety' && a.is_active && (a.effective_from || a.effective_until))
            .map(a => [a.employee_staff_id, {
              start_date: a.effective_from || editRequest.start_date || '',
              end_date: a.effective_until || editRequest.end_date || '',
            }])
        ),
        compliance_override_reason: editRequest.compliance_override_reason || '',
      });
      setEditLoaded(true);
    }
  }, [editRequest, editLoaded]);

  // ─── Data fetches ───────────────────────────────────────────────────────────
  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['active-projects'],
    queryFn: staffAPI.getActiveProjects,
    select: r => r.data,
    staleTime: 5 * 60 * 1000,
  });

  const { data: tradeGroups, isLoading: workersLoading } = useQuery({
    queryKey: ['restday-work-active-workers'],
    queryFn: staffAPI.getRestDayWorkActiveWorkers,
    select: r => r.data,
    staleTime: 60_000,
  });

  const flatWorkers = useMemo(() =>
    (tradeGroups || []).flatMap(g =>
      g.workers.map(w => ({ ...w, trade_category: g.trade_category }))
    ),
    [tradeGroups]
  );

  const { data: safetyPersonnel, isLoading: safetyLoading } = useQuery({
    queryKey: ['restday-work-safety-personnel'],
    queryFn: staffAPI.getRestDayWorkSafetyPersonnel,
    select: r => r.data,
    staleTime: 60_000,
  });

  // ─── Shift calculation ────────────────────────────────────────────────────────
  // Break minutes derive from start/end per MOM rules, matching the
  // backend so the requester sees the same number that gets persisted.
  const breakMinutes = useMemo(
    () => calcMomBreakMinutes(formData.start_time, formData.end_time),
    [formData.start_time, formData.end_time]
  );
  const shiftInfo = useMemo(
    () => calcShiftHours(formData.start_time, formData.end_time, breakMinutes),
    [formData.start_time, formData.end_time, breakMinutes]
  );

  // ─── Project search ─────────────────────────────────────────────────────────
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const projectDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(e.target)) setShowProjectDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    const q = projectSearch.toLowerCase();
    return projects.filter(p =>
      p.project_name?.toLowerCase().includes(q) || p.project_code?.toLowerCase().includes(q)
    );
  }, [projects, projectSearch]);

  const selectProject = (project) => {
    setFormData(prev => ({ ...prev, project: project.project_code, project_name: `${project.project_code} - ${project.project_name}` }));
    setShowProjectDropdown(false);
    setProjectSearch('');
  };

  const clearProject = () => {
    setFormData(prev => ({ ...prev, project: '', project_name: '' }));
  };

  // ─── Compliance check (debounced) ──────────────────────────────────────────
  const hasDates = formData.date_selection_type === 'range'
    ? !!formData.start_date && !!formData.end_date
    : formData.specific_dates.length > 0;

  const complianceDatesKey = formData.date_selection_type === 'range'
    ? `${formData.start_date}|${formData.end_date}`
    : formData.specific_dates.join(',');

  const checkCompliance = useCallback(async (workerIds, fd) => {
    if (workerIds.length === 0) {
      setComplianceWarnings([]);
      return;
    }

    let dates;
    if (fd.date_selection_type === 'range' && fd.start_date && fd.end_date) {
      dates = [];
      const start = new Date(fd.start_date + 'T00:00:00');
      const end = new Date(fd.end_date + 'T00:00:00');
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(d.toISOString().split('T')[0]);
      }
    } else {
      dates = fd.specific_dates;
    }

    if (!dates || dates.length === 0) {
      setComplianceWarnings([]);
      return;
    }

    setComplianceLoading(true);
    try {
      const res = await staffAPI.checkRestDayWorkCompliance({
        worker_ids: workerIds,
        dates,
      });
      setComplianceWarnings(res.data?.warnings || res.data || []);
    } catch {
      setComplianceWarnings([]);
    } finally {
      setComplianceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (complianceTimerRef.current) clearTimeout(complianceTimerRef.current);

    if (formData.worker_ids.length > 0 && hasDates) {
      complianceTimerRef.current = setTimeout(() => {
        checkCompliance(formData.worker_ids, formData);
      }, 600);
    } else {
      setComplianceWarnings([]);
    }

    return () => {
      if (complianceTimerRef.current) clearTimeout(complianceTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.worker_ids, complianceDatesKey, hasDates, checkCompliance]);

  // ─── Mutations ──────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: (data) =>
      isEditMode
        ? staffAPI.updateRestDayWorkRequest(editRequestId, data)
        : staffAPI.createRestDayWorkRequest(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['restday-work'] }),
  });

  const submitMutation = useMutation({
    mutationFn: (id) => staffAPI.submitRestDayWorkRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['restday-work'] });
      toast.success(isEditMode ? 'Amendment submitted for approval' : 'Rest day work request submitted for approval');
      onSuccess?.();
    },
  });

  const handleSaveDraft = async () => {
    try {
      const payload = buildPayload();
      const res = await saveMutation.mutateAsync(payload);
      toast.success(isEditMode ? 'Amendment draft updated' : 'Rest day work draft saved');
      return isEditMode ? editRequestId : res.data?.id;
    } catch (err) {
      const msg = err.response?.data;
      if (typeof msg === 'object' && msg !== null) {
        const firstError = Object.values(msg).flat().join('; ');
        toast.error(firstError || 'Failed to save draft');
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Failed to save draft');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = buildPayload();
      const res = await saveMutation.mutateAsync(payload);
      const id = isEditMode ? editRequestId : res.data?.id;
      if (!id) {
        toast.error('Failed to retrieve request ID after saving. Please try again.');
        return;
      }
      await submitMutation.mutateAsync(id);
    } catch (err) {
      const msg = err.response?.data;
      if (typeof msg === 'object' && msg !== null) {
        const firstError = Object.values(msg).flat().join('; ');
        toast.error(firstError || 'Failed to submit');
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Failed to submit');
      }
    }
  };

  const buildPayload = () => {
    const payload = {
      project: formData.project,
      description: formData.description,
      date_selection_type: formData.date_selection_type,
      start_time: formData.start_time,
      end_time: formData.end_time,
      recovery_type: formData.recovery_type,
      worker_ids: formData.worker_ids,
      safety_ids: formData.safety_ids,
    };
    if (formData.date_selection_type === 'range') {
      payload.start_date = formData.start_date;
      payload.end_date = formData.end_date;
    } else {
      payload.specific_dates = formData.specific_dates;
    }
    if (Object.keys(formData.worker_date_overrides).length > 0) {
      payload.worker_date_overrides = formData.worker_date_overrides;
    }
    if (Object.keys(formData.safety_date_overrides).length > 0) {
      payload.safety_date_overrides = formData.safety_date_overrides;
    }
    if (complianceWarnings.length > 0 && formData.compliance_override_reason) {
      payload.compliance_override_reason = formData.compliance_override_reason;
    }
    return payload;
  };

  // ─── Step validation ────────────────────────────────────────────────────────
  const isStep1Valid = !!formData.project && formData.description.trim().length > 0;
  const isStep2Valid = (
    formData.date_selection_type === 'range'
      ? !!formData.start_date && !!formData.end_date
      : formData.specific_dates.length > 0
  ) && !!formData.start_time && !!formData.end_time;
  const isStep3Valid = formData.worker_ids.length > 0;

  const canSubmit = isStep1Valid && isStep2Valid && isStep3Valid;

  // ─── Specific date add/remove ───────────────────────────────────────────────
  const toggleSpecificDate = (date) => {
    const iso = toLocalISODate(date);
    setFormData(prev => ({
      ...prev,
      specific_dates: prev.specific_dates.includes(iso)
        ? prev.specific_dates.filter(d => d !== iso)
        : [...prev.specific_dates, iso].sort(),
    }));
  };

  const restDayDateCount = formData.date_selection_type === 'range'
    ? (formData.start_date && formData.end_date
        ? Math.ceil((new Date(formData.end_date) - new Date(formData.start_date)) / 86400000) + 1
        : 0)
    : formData.specific_dates.length;

  // ─── Render ─────────────────────────────────────────────────────────────────
  const stepLabels = ['Project & Description', 'Dates & Times', 'Personnel', 'Review & Submit'];

  return (
    <div className="bg-white rounded-2xl shadow-sm border">
      {/* Stepper header */}
      <div className="border-b border-gray-100 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-center gap-2 overflow-x-auto scrollbar-hide">
          {stepLabels.map((label, idx) => {
            const num = idx + 1;
            const isActive = step === num;
            const isCompleted = step > num;
            return (
              <button
                key={num}
                type="button"
                onClick={() => setStep(num)}
                className="flex items-center gap-2 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded-full"
              >
                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 transition-colors ${
                  isActive ? 'bg-blue-800 text-white shadow-sm' :
                  isCompleted ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-200 text-gray-500'
                }`}>
                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : num}
                </span>
                <span className={`hidden sm:inline text-sm font-medium whitespace-nowrap ${
                  isActive ? 'text-gray-900' :
                  isCompleted ? 'text-blue-800' :
                  'text-gray-400'
                }`}>{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {/* Step 1: Project & Description */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3">
              <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p className="text-sm text-blue-800">
                Use this when workers will work on a <strong>rest day or public holiday</strong>. If the shift means they need rest the next morning or next day, set recovery to Full Day or AM Only — choose None only when the next day is a normal working day.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Briefcase className="w-4 h-4 inline mr-1.5" />
                Project
              </label>
              {formData.project ? (
                <div className="flex items-center gap-2 input bg-gray-50">
                  <span className="flex-1 text-sm text-gray-900">{formData.project_name}</span>
                  <button type="button" onClick={clearProject} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="relative" ref={projectDropdownRef}>
                  <input
                    type="text"
                    placeholder={projectsLoading ? 'Loading projects...' : 'Search projects by name or code...'}
                    className="input w-full"
                    value={projectSearch}
                    onChange={e => { setProjectSearch(e.target.value); setShowProjectDropdown(true); }}
                    onFocus={() => setShowProjectDropdown(true)}
                  />
                  {showProjectDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {filteredProjects.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">No projects found</div>
                      ) : (
                        filteredProjects.map(p => (
                          <div
                            key={p.project_code}
                            className="p-2.5 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0"
                            onMouseDown={(e) => { e.preventDefault(); selectProject(p); }}
                          >
                            <div className="text-sm font-medium text-gray-900">{p.project_name}</div>
                            <div className="text-xs text-gray-500 font-mono mt-0.5">{p.project_code}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <FileText className="w-4 h-4 inline mr-1.5" />
                Description of Works
              </label>
              <textarea
                rows={4}
                className="input w-full !h-auto !rounded-xl resize-none"
                placeholder="Describe the rest day work activities, scope, and justification..."
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
        )}

        {/* Step 2: Dates & Times */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Date Selection Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Selection</label>
              <div className="flex gap-3">
                {[{ value: 'range', label: 'Date Range' }, { value: 'specific', label: 'Specific Dates' }].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, date_selection_type: opt.value }))}
                    className={`tab-btn ${formData.date_selection_type === opt.value ? 'active' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {formData.date_selection_type === 'range' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                  <DatePickerField
                    theme="employee"
                    selected={parseLocalDate(formData.start_date)}
                    onChange={d => setFormData(prev => ({ ...prev, start_date: toLocalISODate(d) }))}
                    placeholderText="Select start date"
                    minDate={new Date()}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                  <DatePickerField
                    theme="employee"
                    selected={parseLocalDate(formData.end_date)}
                    onChange={d => setFormData(prev => ({ ...prev, end_date: toLocalISODate(d) }))}
                    placeholderText="Select end date"
                    minDate={parseLocalDate(formData.start_date) || new Date()}
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Pick Dates</label>
                <DatePickerField
                  inline
                  theme="employee"
                  selected={null}
                  onChange={toggleSpecificDate}
                  highlightDates={formData.specific_dates.map(d => parseLocalDate(d))}
                  minDate={new Date()}
                />
                {formData.specific_dates.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {formData.specific_dates.map(d => (
                      <span key={d} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-800 text-xs rounded-full ring-1 ring-inset ring-blue-600/20">
                        {new Date(d + 'T00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'Asia/Singapore' })}
                        <button type="button" onClick={() => toggleSpecificDate(parseLocalDate(d))} className="hover:text-blue-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Time Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  <Clock className="w-3.5 h-3.5 inline mr-1" />Start Time
                </label>
                <select
                  className="input w-full"
                  value={formData.start_time}
                  onChange={e => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                >
                  {WORK_HOURS.map(t => (
                    <option key={`s-${t}`} value={t}>{formatTimeLabel(t)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  <Clock className="w-3.5 h-3.5 inline mr-1" />End Time
                </label>
                <select
                  className="input w-full"
                  value={formData.end_time}
                  onChange={e => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                >
                  {WORK_HOURS.map(t => (
                    <option key={`e-${t}`} value={t}>{formatTimeLabel(t)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Shift info banner */}
            {shiftInfo && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-3">
                <Info className="w-4 h-4 text-blue-700 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p>
                    <strong>{shiftInfo.gross}h</strong> total shift &middot; <strong>{breakMinutes / 60}h</strong> mandatory break{breakMinutes >= 120 ? ` (${breakMinutes / 60} × 1h per MOM)` : ''} &middot; <strong>{shiftInfo.net}h</strong> net working hours
                  </p>
                  {restDayDateCount > 0 && (
                    <p className="mt-1 text-xs text-blue-600">
                      {restDayDateCount} rest day{restDayDateCount !== 1 ? 's' : ''} selected
                      {formData.recovery_type !== 'none'
                        ? ` · ${restDayDateCount} recovery day${restDayDateCount !== 1 ? 's' : ''} (day after each rest-day work date)`
                        : ' · no recovery (next day is a normal working day)'}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Recovery Day — placed after time selection so TL can decide based on shift end time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recovery Day</label>
              <div className="flex gap-3">
                {[
                  { value: 'full_day', label: 'Full Day' },
                  { value: 'am_only', label: 'AM Only' },
                  { value: 'none', label: 'None' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, recovery_type: opt.value }))}
                    className={`tab-btn ${formData.recovery_type === opt.value ? 'active' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {formData.end_time && (
                <p className="text-xs text-gray-500 mt-1">
                  Shift ends at {formatTimeLabel(formData.end_time)}.
                  {formData.recovery_type === 'am_only'
                    ? ' Workers rest the morning, return after lunch.'
                    : formData.recovery_type === 'none'
                      ? ' Next day is a normal working day with full attendance expected.'
                      : ' Workers rest the entire following day.'}
                </p>
              )}
              {formData.recovery_type === 'am_only' && (() => {
                const [eh, em] = (formData.end_time || '').split(':').map(Number);
                if (Number.isFinite(eh) && Number.isFinite(em)) {
                  const endMins = eh * 60 + em;
                  if (endMins >= 600 && endMins <= 780) {
                    return (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                        Shift ends late — workers may not get a meaningful AM rest before the PM session. Consider Full Day.
                      </p>
                    );
                  }
                }
                return null;
              })()}
            </div>
          </div>
        )}

        {/* Step 3: Personnel */}
        {step === 3 && (
          <div className="space-y-6">
            {/* Workers */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Users className="w-4 h-4 inline mr-1.5" />
                Workers / Workmen
              </label>
              <p className="text-xs text-gray-500 mb-2">Select workers for rest day work. Grouped by trade category.</p>
              <SearchableMultiCombobox
                items={flatWorkers}
                value={formData.worker_ids}
                onChange={ids => {
                  setFormData(prev => {
                    const overrides = { ...prev.worker_date_overrides };
                    Object.keys(overrides).forEach(k => { if (!ids.includes(k)) delete overrides[k]; });
                    return { ...prev, worker_ids: ids, worker_date_overrides: overrides };
                  });
                  setCheckedWorkers(prev => {
                    const next = new Set(prev);
                    prev.forEach(id => { if (!ids.includes(id)) next.delete(id); });
                    return next;
                  });
                }}
                displayFn={w => `${w.first_name} ${w.last_name}`}
                valueFn={w => w.staff_id}
                searchFn={(w, term) =>
                  `${w.first_name} ${w.last_name}`.toLowerCase().includes(term) ||
                  w.staff_id.toLowerCase().includes(term)
                }
                groupFn={w => w.trade_category}
                placeholder="Select workers..."
                searchPlaceholder="Search by name or staff ID..."
                emptyLabel="No workers found"
                loading={workersLoading}
                loadingLabel="Loading workers..."
                theme="employee"
                variant="form"
                showChips
                className="w-full"
                renderItem={(w) => (
                  <>
                    <span className="flex-1">{w.first_name} {w.last_name}</span>
                    <span className="text-xs text-gray-400 font-mono">{w.staff_id}</span>
                  </>
                )}
              />
            </div>

            {/* Compliance loading */}
            {complianceLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500 p-3">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-800" />
                Checking rest day compliance...
              </div>
            )}

            {/* Compliance Warnings */}
            {!complianceLoading && complianceWarnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Rest Day Compliance Warnings</p>
                    <p className="text-xs text-amber-700 mt-0.5">The following workers may breach the 12-day consecutive working limit.</p>
                  </div>
                </div>
                {complianceWarnings.map((w, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-amber-100/50 rounded-lg">
                    <span className="text-sm text-amber-900">{w.employee_name}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      w.severity === 'critical' ? 'bg-red-100 text-red-700' :
                      w.severity === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {w.projected_streak} days projected
                    </span>
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-amber-800 mb-1">Override Reason (required to proceed)</label>
                  <textarea
                    rows={2}
                    className="input w-full resize-none text-sm"
                    placeholder="Justify why rest day work is needed despite compliance warnings..."
                    value={formData.compliance_override_reason}
                    onChange={e => setFormData(prev => ({ ...prev, compliance_override_reason: e.target.value }))}
                  />
                </div>
              </div>
            )}

            {/* Worker Date Overrides */}
            {formData.worker_ids.length > 0 && (formData.start_date || formData.specific_dates.length > 0) && (() => {
              const allWorkers = (tradeGroups || []).flatMap(g => g.workers);
              const workerMap = {};
              allWorkers.forEach(w => { workerMap[w.staff_id] = w; });

              const masterLabel = formData.date_selection_type === 'range' && formData.start_date && formData.end_date
                ? `${formatDateShort(formData.start_date)} – ${formatDateShort(formData.end_date)}`
                : formData.specific_dates.length > 0
                  ? `${formData.specific_dates.length} dates`
                  : 'Full period';

              const getWorkerDateLabel = (staffId) => {
                const ov = formData.worker_date_overrides[staffId];
                if (!ov) return null;
                return `${formatDateShort(ov.start_date)} – ${formatDateShort(ov.end_date)}`;
              };

              const allChecked = formData.worker_ids.length > 0 && checkedWorkers.size === formData.worker_ids.length;

              const toggleAll = () => {
                if (allChecked) {
                  setCheckedWorkers(new Set());
                } else {
                  setCheckedWorkers(new Set(formData.worker_ids));
                }
              };

              const toggleCheck = (id) => {
                setCheckedWorkers(prev => {
                  const next = new Set(prev);
                  next.has(id) ? next.delete(id) : next.add(id);
                  return next;
                });
              };

              const masterStart = formData.date_selection_type === 'range' ? parseLocalDate(formData.start_date) : null;
              const masterEnd = formData.date_selection_type === 'range' ? parseLocalDate(formData.end_date) : null;

              const applyBulkDates = () => {
                if (!bulkStartDate || !bulkEndDate) return;
                const startISO = toLocalISODate(bulkStartDate);
                const endISO = toLocalISODate(bulkEndDate);
                const isMasterRange = startISO === formData.start_date && endISO === formData.end_date;
                setFormData(prev => {
                  const overrides = { ...prev.worker_date_overrides };
                  checkedWorkers.forEach(id => {
                    if (isMasterRange) {
                      delete overrides[id];
                    } else {
                      overrides[id] = { start_date: startISO, end_date: endISO };
                    }
                  });
                  return { ...prev, worker_date_overrides: overrides };
                });
                setShowBulkDatePicker(false);
                setCheckedWorkers(new Set());
                setBulkStartDate(null);
                setBulkEndDate(null);
              };

              const clearOverride = (staffId) => {
                setFormData(prev => {
                  const overrides = { ...prev.worker_date_overrides };
                  delete overrides[staffId];
                  return { ...prev, worker_date_overrides: overrides };
                });
              };

              const hasOverrides = Object.keys(formData.worker_date_overrides).length > 0;

              return (
                <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CalendarRange className="w-4 h-4 text-blue-800" />
                      <span className="text-sm font-semibold text-gray-900">Assign Dates</span>
                      <span className="text-xs text-gray-500">({formData.worker_ids.length} worker{formData.worker_ids.length !== 1 ? 's' : ''})</span>
                    </div>
                    {hasOverrides && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        {Object.keys(formData.worker_date_overrides).length} custom
                      </span>
                    )}
                  </div>

                  <div className="px-3 py-2 border-b border-gray-100 flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-500">
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={toggleAll}
                        className="rounded border-gray-300 text-blue-800 focus-visible:ring-blue-500 w-3.5 h-3.5"
                      />
                      Select all
                    </label>
                    {checkedWorkers.size > 0 && (
                      <span className="text-xs text-blue-800 font-medium ml-auto">
                        {checkedWorkers.size} selected
                      </span>
                    )}
                  </div>

                  <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                    {formData.worker_ids.map(id => {
                      const w = workerMap[id];
                      const name = w ? `${w.first_name} ${w.last_name}` : id;
                      const trade = w?.trade_category || '';
                      const customDate = getWorkerDateLabel(id);
                      const isChecked = checkedWorkers.has(id);
                      return (
                        <div
                          key={id}
                          className={`flex items-center gap-2 px-3 py-2 ${isChecked ? 'bg-blue-50/50' : 'hover:bg-white'} transition-colors`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleCheck(id)}
                            className="rounded border-gray-300 text-blue-800 focus-visible:ring-blue-500 w-3.5 h-3.5 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-gray-900 truncate block">{name}</span>
                            {trade && <span className="text-xs text-gray-400">{trade}</span>}
                          </div>
                          {customDate ? (
                            <button
                              type="button"
                              onClick={() => clearOverride(id)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                              title="Custom dates — click to reset to full period"
                            >
                              {customDate}
                              <X className="w-3 h-3" />
                            </button>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {masterLabel}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {checkedWorkers.size > 0 && (
                    <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3">
                      {!showBulkDatePicker ? (
                        <button
                          type="button"
                          onClick={() => {
                            setShowBulkDatePicker(true);
                            setBulkStartDate(masterStart);
                            setBulkEndDate(masterEnd);
                          }}
                          className="btn btn-primary w-full text-sm"
                        >
                          <CalendarRange className="w-4 h-4" />
                          Set Dates for {checkedWorkers.size} Worker{checkedWorkers.size !== 1 ? 's' : ''}
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Custom Date Range</span>
                            <button
                              type="button"
                              onClick={() => { setShowBulkDatePicker(false); setBulkStartDate(null); setBulkEndDate(null); }}
                              className="text-xs text-gray-500 hover:text-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <DatePickerField
                                theme="employee"
                                showIcon={false}
                                selected={bulkStartDate}
                                onChange={d => setBulkStartDate(d)}
                                selectsStart
                                startDate={bulkStartDate}
                                endDate={bulkEndDate}
                                minDate={masterStart}
                                maxDate={masterEnd}
                                placeholderText="Start"
                                className="input w-full text-sm"
                              />
                            </div>
                            <span className="text-gray-400 text-sm">to</span>
                            <div className="flex-1">
                              <DatePickerField
                                theme="employee"
                                showIcon={false}
                                selected={bulkEndDate}
                                onChange={d => setBulkEndDate(d)}
                                selectsEnd
                                startDate={bulkStartDate}
                                endDate={bulkEndDate}
                                minDate={bulkStartDate || masterStart}
                                maxDate={masterEnd}
                                placeholderText="End"
                                className="input w-full text-sm"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={applyBulkDates}
                            disabled={!bulkStartDate || !bulkEndDate}
                            className="btn btn-primary w-full text-sm"
                          >
                            <Check className="w-4 h-4" />
                            Apply to {checkedWorkers.size} Worker{checkedWorkers.size !== 1 ? 's' : ''}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Safety Personnel */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Shield className="w-4 h-4 inline mr-1.5" />
                Safety Personnel
              </label>
              <p className="text-xs text-gray-500 mb-2">Assign safety personnel only when needed.</p>
              <SafetyPersonnelSelect
                personnel={safetyPersonnel}
                selected={formData.safety_ids}
                onChange={ids => setFormData(prev => ({ ...prev, safety_ids: ids }))}
                loading={safetyLoading}
              />
            </div>
          </div>
        )}

        {/* Step 4: Review & Submit */}
        {step === 4 && (
          <div className="space-y-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Sun className="w-4 h-4 text-blue-800" />
              {isEditMode ? 'Amendment Summary' : 'Request Summary'}
            </h3>

            <SummarySection label="Project" value={formData.project_name} />
            <SummarySection label="Description" value={formData.description} multiline />
            <SummarySection
              label="Dates"
              value={
                formData.date_selection_type === 'range'
                  ? `${formData.start_date} to ${formData.end_date} (${restDayDateCount} day${restDayDateCount !== 1 ? 's' : ''})`
                  : `${formData.specific_dates.length} specific date${formData.specific_dates.length !== 1 ? 's' : ''}`
              }
            />
            <SummarySection label="Shift Time" value={`${formatTimeLabel(formData.start_time)} - ${formatTimeLabel(formData.end_time)} (${shiftInfo?.net}h net)`} />

            {/* Workers grouped by date range */}
            {(() => {
              const allWorkers = (tradeGroups || []).flatMap(g => g.workers);
              const workerMap = {};
              allWorkers.forEach(w => { workerMap[w.staff_id] = w; });

              const groups = {};
              formData.worker_ids.forEach(id => {
                const ov = formData.worker_date_overrides[id];
                const key = ov ? `${ov.start_date}|${ov.end_date}` : 'full';
                if (!groups[key]) {
                  groups[key] = {
                    label: ov
                      ? `${formatDateShort(ov.start_date)} – ${formatDateShort(ov.end_date)}`
                      : formData.date_selection_type === 'range'
                        ? `${formatDateShort(formData.start_date)} – ${formatDateShort(formData.end_date)}`
                        : `${formData.specific_dates.length} dates`,
                    isCustom: !!ov,
                    workers: [],
                  };
                }
                const w = workerMap[id];
                groups[key].workers.push(w ? `${w.first_name} ${w.last_name}` : id);
              });

              const groupList = Object.values(groups).sort((a, b) => a.isCustom - b.isCustom);

              return (
                <div>
                  <div className="text-xs font-medium text-gray-500 mb-2">Workers ({formData.worker_ids.length})</div>
                  <div className="space-y-2">
                    {groupList.map((g, i) => (
                      <div key={i} className={`rounded-xl border px-3 py-2 ${g.isCustom ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${g.isCustom ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-800'}`}>
                            {g.label}
                          </span>
                          <span className="text-xs text-gray-500">{g.workers.length} worker{g.workers.length !== 1 ? 's' : ''}</span>
                        </div>
                        <p className="text-sm text-gray-700">{g.workers.join(', ')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <SummarySection label="Safety" value={`${formData.safety_ids.length} safety officer${formData.safety_ids.length !== 1 ? 's' : ''}`} />

            {/* Compliance warnings in review */}
            {complianceWarnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Rest Day Compliance Warnings</p>
                    <p className="text-xs text-amber-700 mt-0.5">The following workers may breach the 12-day consecutive working limit.</p>
                  </div>
                </div>
                {complianceWarnings.map((w, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-amber-100/50 rounded-lg">
                    <span className="text-sm text-amber-900">{w.employee_name}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      w.severity === 'critical' ? 'bg-red-100 text-red-700' :
                      w.severity === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {w.projected_streak} days projected
                    </span>
                  </div>
                ))}
                {formData.compliance_override_reason && (
                  <div className="bg-white border border-amber-200 rounded-lg p-3">
                    <div className="text-xs font-medium text-amber-800 mb-1">Override Reason</div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{formData.compliance_override_reason}</p>
                  </div>
                )}
              </div>
            )}

            {!canSubmit && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-start gap-3">
                <AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-orange-700">Please complete all required fields before submitting.</p>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-4 border-t border-gray-100">
          <div>
            {step > 1 && (
              <button type="button" onClick={() => setStep(step - 1)} className="btn btn-secondary">
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {step < totalSteps ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                className="btn btn-primary"
                disabled={
                  (step === 1 && !isStep1Valid) ||
                  (step === 2 && !isStep2Valid) ||
                  (step === 3 && !isStep3Valid)
                }
              >
                Continue
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSaveDraft}
                  disabled={!canSubmit || saveMutation.isPending}
                  className="btn btn-secondary"
                >
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Save Changes' : 'Save Draft'}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit || saveMutation.isPending || submitMutation.isPending}
                  className="btn btn-primary"
                >
                  <Send className="w-4 h-4" />
                  {saveMutation.isPending
                    ? 'Saving...'
                    : submitMutation.isPending
                      ? 'Submitting...'
                      : isEditMode ? 'Submit Amendment' : 'Submit for Approval'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const SummarySection = ({ label, value, multiline }) => (
  <div className="flex gap-4">
    <span className="text-xs font-medium text-gray-500 w-24 flex-shrink-0 pt-0.5">{label}</span>
    <span className={`text-sm text-gray-900 ${multiline ? 'whitespace-pre-wrap' : ''}`}>{value || '—'}</span>
  </div>
);

export default RestDayWorkRequestForm;
